import { BaseService } from '@/infrastructure/api/base.service'
import type { IAuthService, LoginRequest, LoginResponse } from '@/domain/interfaces/IAuthService'
import { getCsrfTokenFromCookie } from '@/infrastructure/api/utils/csrf'
import { httpClient } from '@/infrastructure/api/http-client'
import { API_CONFIG } from '@/config/api.config'

export class AuthService extends BaseService implements IAuthService {
  constructor() {
    super('')
  }

  async login(request: LoginRequest): Promise<LoginResponse> {
    const csrfToken = getCsrfTokenFromCookie()
    const formData = new URLSearchParams()
    formData.append('Email', request.email)
    formData.append('Password', request.password)
    formData.append('RememberMe', String(request.rememberMe ?? false))
    formData.append('__RequestVerificationToken', csrfToken)

    const response = await httpClient.post<LoginResponse>('/api/auth/login', formData, {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    })
    return response.data
  }

  async logout(): Promise<void> {
    const csrfToken = getCsrfTokenFromCookie()
    const formData = new URLSearchParams()
    formData.append('__RequestVerificationToken', csrfToken)
    await httpClient.post('/Account/LogOff', formData, {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    })
  }

  async renewSession(): Promise<boolean> {
    const response = await httpClient.post<boolean>('/Account/RenewSession', null, {
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
    })
    return response.data === true
  }

  async checkSession(): Promise<boolean> {
    try {
      return await this.renewSession()
    } catch {
      return false
    }
  }

  // Whether this browser still holds a signed-in session. RenewSession can't tell: it answers
  // false both when the session is gone and when the user signed in elsewhere. So ask an
  // [Authorize] endpoint without following redirects: signed in gets a 200, signed out gets
  // the OIDC challenge (a 302 to the identity server), which fetch reports as an opaque
  // redirect without ever contacting the identity server. fetch, because XHR (axios) always
  // follows redirects. null = no answer (offline, server error).
  async hasSignedInSession(): Promise<boolean | null> {
    try {
      const response = await fetch(`${API_CONFIG.baseUrl}/V2/User/GetUser`, {
        credentials: 'include',
        redirect: 'manual',
        headers: { Accept: 'application/json' },
      })
      if (response.type === 'opaqueredirect' || response.status === 401) return false
      return response.ok ? true : null
    } catch {
      return null
    }
  }
}
